import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Button,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TodayIcon from '@mui/icons-material/Today';
import EventIcon from '@mui/icons-material/Event';
import HttpsIcon from '@mui/icons-material/Https';
import EventNoteIcon from '@mui/icons-material/EventNote';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import WalletIcon from '@mui/icons-material/Wallet';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import currency from 'currency.js';
import * as wasi from 'node:wasi';
import QdeliveryApi, {
  GetImpersonationRes,
} from '../../services/api/qdelivery/qdeliveryapi';
import useAuth from '../../hooks/useAuth';
import useProfile from '../../hooks/useProfile';
import useBranches from '../../hooks/useBranches';
import ConfirmationModal from '../ConfirmationModal';
import keyHasPermission from '../../helpers/keyHasPermission';
import ExpenseAddModal from '../../pages/Operations/ExpenseManagement/Modals/ExpenseAddModal';
import AccountLanguage from './AccountLanguage';
import HelpCenter from './HelpCenter';
import AccountMenu from './AccountMenu';
import Pusher, { Channel } from 'pusher-js';

interface Props {
  getExpenseModalData: () => void;
  onClose: () => void;
  expenseAddModal: boolean;
  token: string | null;
}
const currencySymbols: { [key: string]: string } = {
  TRY: '₺',
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  AUD: 'A$',
  CAD: 'C$',
  INR: '₹',
};

function TopBarIcons({
  getExpenseModalData,
  onClose,
  expenseAddModal,
  token,
}: Props) {
  const { t } = useTranslation('layout');
  const { logoutWithPin } = useAuth();
  const { meProfile } = useProfile();
  const { updateBranchesStartDate, updateBranchesEndDate } = useBranches();

  const [deleteConfirmation, setDeleteConfirmation] = useState<string | null>(
    null,
  );
  const [openModal, setOpenModal] = useState(false);
  const [data, setData] = useState<GetImpersonationRes | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(true);
  const [wallet, setWalletData] = useState<{
    currency_unit: string;
    amount: string;
    amount_int: number;
  } | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  let pusher: Pusher | null = null;
  let channel: Channel | null = null;

  const fetchData = () => {
    if (meProfile?.id) {
      const appKey = process.env.REACT_APP_PUSHER_APP_KEY || '';

      pusher = new Pusher(appKey, {
        cluster: 'eu',
      });
      const channelName = `branch.${meProfile?.activeBranch?.id}`;

      channel = pusher.subscribe(channelName);

      channel.bind('wallet.updated', x =>
        setWalletData({
          amount: x.amount,
          amount_int: x.amount_int,
          currency_unit: x.currency_unit,
        }),
      );

      return () => {
        if (channel) {
          channel.unbind('wallet.updated', x =>
            console.log('wallet.updated', x),
          );
          if (pusher?.connection.state === 'connected') {
            // @ts-ignore
            pusher.unsubscribe(channelName);
          }
        }
        if (pusher && pusher.connection.state === 'connected') {
          pusher.disconnect();
        }
      };
    }
  };
  useEffect(() => {
    fetchData();
  }, [meProfile?.activeBranch?.id]);

  useEffect(() => {
    const fetchWalletData = async () => {
      try {
        const walletData = await QdeliveryApi.getQdeliveryWallet();

        setWalletData(walletData);
      } catch (err) {
        console.error('Failed to fetch wallet data', err);
      }
    };
    fetchWalletData();
  }, []);

  useEffect(() => {
    const fetchWalletData = async () => {
      try {
        const walletData = await QdeliveryApi.getQdeliveryWallet();
        setWalletData(walletData);
      } catch (err) {
        console.error('Failed to fetch wallet data', err);
      }
    };

    // if (meProfile?.workingTypes) {
    //   fetchWalletData();
    //   const intervalId = setInterval(fetchWalletData, 5000);
    //   return () => clearInterval(intervalId);
    // }
  }, [meProfile?.workingTypes]);

  useEffect(() => {
    //
    // // fetchData(); // Initial fetch
    // const intervalId = setInterval(fetchData, 5000); // Fetch every 5 seconds
    //
    // return () => clearInterval(intervalId); // Cleanup interval on unmount
  }, [meProfile?.activeBranch?.id]);

  const handleOpenModal = async () => {
    setOpenModal(true);
    setLoading(true);
    try {
      const response = await QdeliveryApi.getImpersonation();
      setData(response.data);
    } catch (err) {
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };
  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const hasEndOfDayPermission = keyHasPermission(
    'end-of-day',
    meProfile?.permissions || [],
  );
  const hasCreateExpensePermission = keyHasPermission(
    'create-expense',
    meProfile?.permissions || [],
  );
  const handleFullscreenToggle = () => {
    setIsFullscreen(false);
    const docEl = document.documentElement;

    if (!document.fullscreenElement) {
      if (docEl.requestFullscreen) {
        docEl.requestFullscreen().catch(err => {
          console.error('Fullscreen request failed:', err);
        });
      } else if ((docEl as any).mozRequestFullScreen) {
        (docEl as any).mozRequestFullScreen();
      } else if ((docEl as any).webkitRequestFullscreen) {
        (docEl as any).webkitRequestFullscreen();
      } else if ((docEl as any).msRequestFullscreen) {
        (docEl as any).msRequestFullscreen();
      } else {
        console.error('Fullscreen API is not supported by this browser.');
      }
    }
  };

  const handleFullscreenExit = () => {
    setIsFullscreen(true);
    if (
      document.fullscreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).mozFullScreenElement ||
      (document as any).msFullscreenElement
    ) {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(err => {
          console.error('Fullscreen exit failed:', err);
        });
      } else if ((document as any).mozCancelFullScreen) {
        (document as any).mozCancelFullScreen();
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      } else if ((document as any).msExitFullscreen) {
        (document as any).msExitFullscreen();
      } else {
        console.error('Fullscreen API exit is not supported by this browser.');
      }
    } else {
      console.log('No element is currently in fullscreen mode.');
    }
  };

  return (
    <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
      {wallet ? (
        <Tooltip title={t('topbar.wallet')}>
          <div
            onClick={() => handleOpenModal()}
            onKeyDown={e => {
              if (e.key === 'Enter') handleOpenModal();
            }}
            role="button"
            tabIndex={0}
            style={{
              border: '1px solid grey',
              height: '30%',
              marginTop: '17px',
              borderRadius: '7px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              paddingLeft: '5px',
              paddingRight: '8px',
              cursor: 'pointer',
            }}
          >
            <IconButton
              size="medium"
              aria-label="show 17 new notifications"
              sx={{
                color: grey[700],
                borderRadius: 0,
                padding: '0px',
                margin: '3px',
              }}
              disabled={Boolean(token)}
            >
              <WalletIcon />
            </IconButton>
            <Typography
              sx={{
                color: grey[700],
                fontWeight: 500,
                marginTop: '1px',
                paddingLeft: '5px',
              }}
            >
              {wallet
                ? currency(wallet.amount_int / 100, {
                    symbol:
                      currencySymbols[wallet.currency_unit] ||
                      wallet.currency_unit,
                  }).format()
                : null}
            </Typography>
          </div>
        </Tooltip>
      ) : null}

      {hasCreateExpensePermission && (
        <Tooltip title={t('topbar.fastExpense')}>
          <IconButton
            size="large"
            aria-label="show 17 new notifications"
            sx={{ color: grey[700], borderRadius: 0 }}
            onClick={() => getExpenseModalData()}
            disabled={Boolean(token)}
          >
            <TrendingDownIcon />
          </IconButton>
        </Tooltip>
      )}
      {hasEndOfDayPermission &&
        (meProfile?.activeBranch?.day_id === null ? (
          <Tooltip title={t('topbar.start_Day')}>
            <IconButton
              size="large"
              aria-label="show 17 new notifications"
              sx={{ color: grey[700], borderRadius: 0 }}
              onClick={() => setDeleteConfirmation('start-date')}
              disabled={Boolean(token)}
            >
              <TodayIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title={t('topbar.end_Day')}>
            <IconButton
              size="large"
              aria-label="show 17 new notifications"
              sx={{ color: grey[700], borderRadius: 0 }}
              onClick={() => setDeleteConfirmation('end-date')}
              disabled={Boolean(token)}
            >
              <EventIcon />
            </IconButton>
          </Tooltip>
        ))}
      <Tooltip title={t('topbar.lock')}>
        <IconButton
          size="large"
          aria-label="show 17 new notifications"
          sx={{ color: grey[700], borderRadius: 0 }}
          onClick={logoutWithPin}
          disabled={Boolean(token)}
        >
          <HttpsIcon />
        </IconButton>
      </Tooltip>
      {meProfile?.installed_apps?.find(
        item => item.slug === 'customer-display-screen',
      ) && (
        <Tooltip title={t('topbar.display')}>
          <IconButton
            size="large"
            aria-label="show 17 new notifications"
            sx={{ color: grey[700], borderRadius: 0 }}
            onClick={() => {
              window.open(
                '/customer-display',
                '_blank',
                'rel=noopener noreferrer',
              );
            }}
            disabled={Boolean(token)}
          >
            <EventNoteIcon />
          </IconButton>
        </Tooltip>
      )}
      <HelpCenter />
      <AccountLanguage />
      {isFullscreen ? (
        <Tooltip title="Tam Ekran">
          <IconButton
            size="large"
            aria-label="toggle fullscreen"
            sx={{ color: grey[700], borderRadius: 0 }}
            onClick={handleFullscreenToggle}
          >
            <FullscreenIcon />
          </IconButton>
        </Tooltip>
      ) : null}
      {!isFullscreen ? (
        <Tooltip title="Tam Ekrandan Çık">
          <IconButton
            size="large"
            aria-label="toggle fullscreen"
            sx={{ color: grey[700], borderRadius: 0 }}
            onClick={handleFullscreenExit}
          >
            <FullscreenExitIcon />
          </IconButton>
        </Tooltip>
      ) : null}

      <AccountMenu />

      <ConfirmationModal
        title={
          deleteConfirmation === 'start-date'
            ? t('topbar.confirmation.startDayTitle')
            : t('topbar.confirmation.endDayTitle')
        }
        titleIconName="QuestionMarkOutlined"
        description={
          deleteConfirmation === 'start-date'
            ? t('topbar.confirmation.startDaySubtitle')
            : t('topbar.confirmation.endDaySubtitle')
        }
        open={!!deleteConfirmation}
        onClose={() => setDeleteConfirmation(null)}
        positiveAction={t('topbar.confirmation.confirm')}
        onPositiveClick={() => {
          if (deleteConfirmation === 'start-date') {
            updateBranchesStartDate(meProfile?.activeBranch?.id || '');
          }
          if (deleteConfirmation === 'end-date') {
            updateBranchesEndDate(meProfile?.activeBranch?.id || '');
          }
          setDeleteConfirmation(null);
        }}
        negativeAction={t('topbar.confirmation.cancel')}
        onNegativeClick={() => setDeleteConfirmation(null)}
      />
      <ExpenseAddModal open={expenseAddModal} onClose={onClose} item={null} />
      <Dialog open={openModal} onClose={handleCloseModal} fullScreen>
        <DialogTitle
          sx={{
            backgroundColor: 'black',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            sx={{ color: 'white', display: 'flex', alignItems: 'center' }}
          >
            <WalletIcon />{' '}
            <Typography
              sx={{ marginTop: '6px', marginLeft: '4px' }}
              variant="h6"
              gutterBottom
            >
              {wallet
                ? currency(wallet.amount_int / 100, {
                    symbol:
                      currencySymbols[wallet.currency_unit] ||
                      wallet.currency_unit,
                  }).format()
                : null}
            </Typography>
          </Typography>
          <Button
            variant="contained"
            color="success"
            onClick={handleCloseModal}
          >
            Geri Dön
          </Button>
        </DialogTitle>
        <DialogContent sx={{ margin: '0px', padding: '0px' }}>
          {data ? (
            <iframe
              src={`${process.env.REACT_APP_QDELIVERY_LINK_WALLET}=${data.session_id}`}
              width="100%"
              height="100%"
              style={{ border: 'none' }}
              title="Uygulamalar"
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default TopBarIcons;
